import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  Switch, TextField, IconButton, Button, Typography, CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import AddIcon from "@material-ui/icons/Add";
import { Grid } from "@material-ui/core";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
    gap: theme.spacing(3),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "900px",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "0.5px",
  },
  addButton: {
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  confidenceFieldWrapper: {
    marginTop: theme.spacing(1),
    width: "100%",
    maxWidth: "1600px",
  },
  confidenceLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  requiredAsterisk: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": { color: "#007bff" },
    "&.Mui-checked + .MuiSwitch-track": { backgroundColor: "#007bff" },
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": { backgroundColor: "#d4551a" },
  },
  resetChangesButton: {
    border: "1px solid #000",
    color: "#000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
}));

const DigitalizationCodesTab = (props) => {
  const { featuresAccess, sponsorId } = props;
  const classes = useStyles();
  const [digitalizationCodes, setDigitalizationCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [savingStates, setSavingStates] = useState({});
  const [lowConfidenceLevel, setLowConfidenceLevel] = useState("");
  const [mediumConfidenceLevel, setMediumConfidenceLevel] = useState({ min: "", max: "" });
  const [highConfidenceLevel, setHighConfidenceLevel] = useState("");
  const [updatingConfidence, setUpdatingConfidence] = useState(false);
  const [originalOrgSettings, setOriginalOrgSettings] = useState(null);
  
  
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const fetchDigitalizationCodes = async () => {
    setError(null);
    setLoading(true);
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDigitalizationCodes`;
      const response = await axios.get(url);
      const data = response?.data;
      if (data?.status) {
        const formatted = (data.digitalizationCodesList || []).map((item, index) => ({
          id: item.digitalizationCodeID || `ID-${index + 1}`,
          code: item.digitalizationCode || "",
          description: item.digitalizationCodeDescription || "",
          selected: !!item.activated,
          isNew: false,
          type: item.digitalizationCodeType,
        }));
        setDigitalizationCodes(formatted);
      } else {
        setError(data?.message || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgLevelSettings = async () => {
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchOrgLevelSettings`;
      const resp = await axios.get(url);

      if (resp.data?.orgLevelSettings) {
        const obj = resp.data.orgLevelSettings;

        setLowConfidenceLevel(obj.lowConfidenceLimit || "");
        setMediumConfidenceLevel({
          min: obj.mediumConfidenceLimitMinValue || "",
          max: obj.mediumConfidenceLimitMaxValue || ""
        });
        setHighConfidenceLevel(obj.highConfidenceLimit || "");

        setOriginalOrgSettings({
          id: obj.id,
          low: obj.lowConfidenceLimit || "",
          medMin: obj.mediumConfidenceLimitMinValue || "",
          medMax: obj.mediumConfidenceLimitMaxValue || "",
          high: obj.highConfidenceLimit || ""
        });
      }
    } catch (e) {
      console.error("Error fetching org level settings:", e);
    }
  };

  useEffect(() => { 
    fetchDigitalizationCodes(); 
    fetchOrgLevelSettings();
  }, []);

  const saveDigitalizationCode = async (index) => {
    const codeToSave = digitalizationCodes[index];
    if (!codeToSave.code?.trim() || !codeToSave.description?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        digitalizationCodesList: [{
          digitalizationCode: codeToSave.code,
          digitalizationCodeDescription: codeToSave.description,
          digitalizationCodeType: "New",
          activated: codeToSave.selected,
        }],
      };
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addDigitalizationCodes`;
      const response = await axios.post(url, payload);
      fetchDigitalizationCodes();
      if (response.data?.status) {
        if (props.setDigitalizationCode) {
          props.setDigitalizationCode((prev) => !prev);
        }
      } else {
        setError(response.data?.message || "Failed to save digitalization code.");
      }
    } catch {
      setError("An error occurred while saving the digitalization code.");
    } finally {
      setSavingStates((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleDelete = async (index) => {
    const toDelete = digitalizationCodes[index];
    if (toDelete.isNew) {
      setDigitalizationCodes((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    try {
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        digitalizationCodesList: [{
          digitalizationCodeID: toDelete.id,
          digitalizationCode: toDelete.code,
          activated: toDelete.selected,
          markForDelete: true,
        }],
      };
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDigitalizationCodes`;
      await axios.post(url, payload);
      fetchDigitalizationCodes();
    } catch {
      setError("An error occurred while deleting the digitalization code.");
    }
  };

  const handleToggle = (index) => {
    const updated = [...digitalizationCodes];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setDigitalizationCodes(updated);
    setIsUpdated(true);
  };

  const handleCodeChange = (index, value) => {
    const updated = [...digitalizationCodes];
    updated[index].code = value;
    setDigitalizationCodes(updated);
    setIsUpdated(true);
  };

  const handleDescriptionChange = (index, value) => {
    const updated = [...digitalizationCodes];
    updated[index].description = value;
    setDigitalizationCodes(updated);
    setIsUpdated(true);
  };

  const handleAddCode = () => {
    setDigitalizationCodes([...digitalizationCodes, { code: "", description: "", selected: false, isNew: true }]);
  };

  const handleCancel = (index) => {
    setDigitalizationCodes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveChanges = async () => {
    setErrorMessage("");
    setUpdatingConfidence(false);

    try {
      if (
        lowConfidenceLevel === "" ||
        mediumConfidenceLevel.min === "" ||
        mediumConfidenceLevel.max === "" ||
        highConfidenceLevel === ""
      ) {
        setErrorMessage("Please fill all confidence level values.");
        return;
      }

      const low = Number((lowConfidenceLevel ?? "").toString().trim());
      const medMin = Number((mediumConfidenceLevel.min ?? "").toString().trim());
      const medMax = Number((mediumConfidenceLevel.max ?? "").toString().trim());
      const high = Number((highConfidenceLevel ?? "").toString().trim());

      if ([low, medMin, medMax, high].some((v) => isNaN(v))) {
        setErrorMessage("Confidence level values must be numbers only.");
        return;
      }

      if (low >= high) {
        setErrorMessage("Low Confidence must be less than high confidence level");
        return;
      }
      if (medMin >= medMax) {
        setErrorMessage("Medium Min confidence level must be less than Medium Max confidence value.");
        return;
      }

      setErrorMessage("");

      const updatedCodes = digitalizationCodes.filter((d) => d.isUpdated);
      if (updatedCodes.length) {
        if (digitalizationCodes.every((d) => !d.selected)) {
          setErrorMessage("Please enable at least one code.");
          setUpdatingConfidence(false);
          return;
        }

        const codesPayload = {
          sponsorId: sponsorId,
          ecosystemId: localStorage.getItem("ecosystemId"),
          digitalizationCodesList: updatedCodes.map((d) => ({
            digitalizationCodeID: d.id,
            digitalizationCode: d.code,
            digitalizationCodeDescription: d.description,
            activated: d.selected,
          })),
        };

        const updateCodesUrl = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDigitalizationCodes`;
        const updateCodesResp = await axios.post(updateCodesUrl, codesPayload);

        if (!(updateCodesResp.data?.status === true || updateCodesResp.data?.status === "true")) {
          setErrorMessage(updateCodesResp.data?.message || "Failed to update digitalization codes.");
          setUpdatingConfidence(false);
          return;
        }

        await fetchDigitalizationCodes();
        setIsUpdated(false);
      }

      if (
        originalOrgSettings &&
        (
          lowConfidenceLevel !== originalOrgSettings.low ||
          mediumConfidenceLevel.min !== originalOrgSettings.medMin ||
          mediumConfidenceLevel.max !== originalOrgSettings.medMax ||
          highConfidenceLevel !== originalOrgSettings.high
        )
      ) {
        setUpdatingConfidence(true);

        const updatePayload = {
          id: originalOrgSettings.id,
          organization_id: sponsorId,
          ecosystem_id: localStorage.getItem("ecosystemId"),
          sponsorId: sponsorId,
          lowConfidenceLimit: low,
          mediumConfidenceLimitMinValue: medMin,
          mediumConfidenceLimitMaxValue: medMax,
          highConfidenceLimit: high,
        };

        const updateUrl = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateOrgLevelSettings`;
        const updateResp = await axios.post(updateUrl, updatePayload);

        setUpdatingConfidence(false);

        if (!(updateResp.data?.status === true || updateResp.data?.status === "true")) {
          setErrorMessage(updateResp.data?.message || "Failed to update Org Level Settings.");
          return;
        }

        setOriginalOrgSettings({
          id: originalOrgSettings.id,
          low: lowConfidenceLevel,
          medMin: mediumConfidenceLevel.min,
          medMax: mediumConfidenceLevel.max,
          high: highConfidenceLevel,
        });
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setError("Something went wrong during Save Changes.");
      setUpdatingConfidence(false);
    }
  };


  const handleResetChanges = () => {
    if (isUpdated) {
      fetchDigitalizationCodes();
      if (originalOrgSettings) {
        setLowConfidenceLevel(originalOrgSettings.low || "");
        setMediumConfidenceLevel({
          min: originalOrgSettings.medMin || "",
          max: originalOrgSettings.medMax || ""
        });
        setHighConfidenceLevel(originalOrgSettings.high || "");
      }
      setIsUpdated(false);
      setErrorMessage("");
    }
  };

  return (
    <div className={classes.container}>
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
        <Table className={`${classes.table} default-table-styles`}>
          <TableHead>
            <TableRow>
              <TableCell>Selection</TableCell>
              <TableCell>Digitalization Code</TableCell>
              <TableCell>Digitalization Code Description</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4}><Typography>{error}</Typography></TableCell>
              </TableRow>
            ) : (
              digitalizationCodes.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.bodyCell}>
                    <Switch checked={item.selected} onChange={() => handleToggle(index)} classes={{ switchBase: classes.switchBase }} disabled={!canEdit} />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <TextField
                        placeholder="Enter Code"
                        value={item.code}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onBlur={(e) => {
                          const { value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleCodeChange(index, value.trim());
                          }
                        }}
                        className={classes.textField} 
                        variant="outlined"
                        disabled={!canEdit}
                      />
                    ) : item.code}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Enter Description"
                          value={item.description}
                          onChange={(e) => handleDescriptionChange(index, e.target.value)}
                          onBlur={(e) => {
                            const { value } = e.target;
                            // Trim leading and trailing spaces for string values
                            if (typeof value === "string" && value.trim() !== value) {
                              handleDescriptionChange(index, value.trim());
                            }
                          }}
                          className={classes.textField} 
                          variant="outlined"
                          disabled={!canEdit}
                        />
                        <div className={classes.inlineActions}>
                          <IconButton style={{padding: 0}} onClick={() => handleCancel(index)}><CancelIcon fontSize="small" /></IconButton>
                         <IconButton
                            style={{padding: 0}}
                            onClick={() => saveDigitalizationCode(index)}
                            disabled={
                              !item.code?.trim() ||
                              !item.description?.trim() ||
                              digitalizationCodes.some(
                                (f, i) =>
                                  i !== index &&
                                  f.code?.trim().toLowerCase() === item.code?.trim().toLowerCase()
                              ) ||
                              savingStates[index]
                            }
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : item.description}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {canEdit && (item.isNew || item.type === "New") && (
                      <IconButton onClick={() => handleDelete(index)}><DeleteIcon className={classes.deleteIcon} /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div className={classes.footer}>
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit}
            style={{ opacity: !isUpdated || !canEdit ? 0.7 : 1 }}
          >
            Save Selection
          </Button>
          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
            disabled={!isUpdated || !canEdit}
            style={{ opacity: !isUpdated || !canEdit ? 0.8 : 1 }}
          >
            Reset Selection
          </Button>
        </div>
      </div>

      {canEdit && (
        <Button variant="outlined" onClick={handleAddCode} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Digitalization Code
        </Button>
      )}

        <div style={{ width: "100%", maxWidth: "1600px", marginTop: 12 }}>
          {errorMessage && <div style={{ color: "red" }}>{errorMessage}</div>}
          {updatingConfidence && (
              <div style={{ marginTop: 10 }}>
                  <CircularProgress size={20} /> Updating...
              </div>
           )}
          <Grid container spacing={4} className={classes.confidenceFieldWrapper}>
            {/* Low Confidence */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.confidenceLabel}>
                Low Confidence Level (&lt;/=) % <span className={classes.requiredAsterisk}>*</span>
              </Typography>
              <TextField
                style={{ width: "380px" }}
                value={lowConfidenceLevel}
                onChange={(e) => {
                  setLowConfidenceLevel(e.target.value);
                  setIsUpdated(true);
                }}
                variant="outlined"
                className={classes.textField}
                placeholder="Enter Low Confidence"
                disabled={!canEdit}
                inputProps={{
                  onBeforeInput: (e) => { if (!/^[0-9.]$/.test(e.data)) e.preventDefault(); }
                }}
              />
            </Grid>

            {/* Medium Confidence */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.confidenceLabel}>
                Medium Confidence Level (between) % <span className={classes.requiredAsterisk}>*</span>
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <TextField
                  value={mediumConfidenceLevel.min || ""}
                  onChange={(e) => {
                    setMediumConfidenceLevel(prev => ({ ...prev, min: e.target.value }));
                    setIsUpdated(true);
                  }}
                  variant="outlined"
                  placeholder="Min"
                  disabled={!canEdit}
                  inputProps={{
                    onBeforeInput: (e) => { if (!/^[0-9.]$/.test(e.data)) e.preventDefault(); }
                  }}
                  className={classes.textField}
                  style={{ width: "120px" }}
                />
                <Typography>to</Typography>
                <TextField
                  value={mediumConfidenceLevel.max || ""}
                  onChange={(e) => {
                    setMediumConfidenceLevel(prev => ({ ...prev, max: e.target.value }));
                    setIsUpdated(true);
                  }}
                  variant="outlined"
                  placeholder="Max"
                  disabled={!canEdit}
                  inputProps={{
                    onBeforeInput: (e) => { if (!/^[0-9.]$/.test(e.data)) e.preventDefault(); }
                  }}
                  className={classes.textField}
                  style={{ width: "120px" }}
                />
              </div>
            </Grid>

            {/* High Confidence */}
            <Grid item xs={12} sm={6}>
              <Typography className={classes.confidenceLabel}>
                High Confidence Level (&gt;/=) % <span className={classes.requiredAsterisk}>*</span>
              </Typography>
              <TextField
                style={{ width: "380px" }}
                value={highConfidenceLevel}
                onChange={(e) => {
                  setHighConfidenceLevel(e.target.value);
                  setIsUpdated(true);
                }}
                variant="outlined"
                className={classes.textField}
                placeholder="Enter High Confidence"
                disabled={!canEdit}
                inputProps={{
                  onBeforeInput: (e) => { if (!/^[0-9.]$/.test(e.data)) e.preventDefault(); }
                }}
              />
            </Grid>
          </Grid>
        </div>
    </div>
  );
};

export default DigitalizationCodesTab;
